import uniqueId from 'lodash/uniqueId';

import type { ChartFormValues } from '@/features/Dashboard/context/EditChartApp';
import type { BasicTableValues, TileValues } from '@/features/Dashboard/context/EditChartApp/types';
import {
  balancedPalette,
  chartPaletteSelection,
  ColorPalette,
  monoBluePalette,
  vibrantPalette
} from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import type {
  FWDChartWidgetV2,
  MarketApp,
  SeasonalChartWidgetV2,
  SimpleChartWidgetV2,
  TableWidgetV2,
  TilesWidgetV2,
  WidgetV2
} from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';
import type { FWDTemplateChartSeries, TemplateChartSeries } from '@/types/charts';

type SymbolKeyLike = { Code?: string; PeriodType?: string; Period?: string; TimeRef?: string };
type SeriesLike = { shortTitle?: string; symbol?: { key?: SymbolKeyLike } };

const newChart = (values: ChartFormValues) => {
  const palette = chartPaletteSelection.find(c => c.id === values.colorPalette);

  const getColor = (index: number) => {
    const paletteColors = palette?.palette ?? [];
    const paletteIndex = paletteColors.length > 0 ? index % paletteColors.length : 0;
    const colorHex = paletteColors[paletteIndex]?.hex ?? '#000000';
    const colorName = paletteColors[paletteIndex]?.name ?? 'default';
    return { colorHex, colorName };
  };

  const isFWD = values.type === AppType.FWD;

  const series = values.symbols.active.map((symbol, index) => {
    const { colorHex, colorName } = getColor(index);
    const baseKey = {
      Code: symbol.symbol.Code,
      PeriodType: symbol.symbol.PeriodType,
      TimeRef: symbol.symbol.TimeRef
    };
    return {
      settings: { type: 'line' as const, color: colorHex, colorName },
      shortTitle: symbol.shortTitle,
      symbol: {
        key: isFWD ? baseKey : { ...baseKey, Period: symbol.symbol.Period },
        group: 'Prod_Indexes' as const,
        columns: null
      }
    };
  }) as TemplateChartSeries[] | FWDTemplateChartSeries[];

  let metadata: { graphType: 'seasonal'; from: string } | { graphType: 'forwardSimple' } | null =
    null;
  if (values.type === AppType.Seasonal) {
    metadata = { graphType: 'seasonal' as const, from: values.from ? `${values.from}y` : '2y' };
  } else if (isFWD) {
    metadata = { graphType: 'forwardSimple' as const };
  }

  const tab = {
    name: values.name,
    metadata,
    series,
    type: 'graph' as const
  } as SimpleChartWidgetV2 | SeasonalChartWidgetV2 | FWDChartWidgetV2;

  return {
    id: uniqueId(new Date().toISOString()),
    tabs: [tab]
  };
};

const newTable = (values: BasicTableValues): MarketApp => {
  const { symbols, name } = values;
  const rows = symbols.active.map(s => {
    return {
      shortTitle: s.shortTitle,
      symbol: { key: { ...s.symbol }, group: s.group },
      diffSymbol: {
        group: s.group,
        key: {
          Code: '',
          PeriodType: '',
          Period: '',
          TimeRef: ''
        }
      }
    };
  });
  return {
    id: uniqueId(new Date().toISOString()),
    tabs: [
      {
        name,
        layout: null,
        metadata: null,
        type: 'table',
        sections: [
          {
            name: '',
            rows
          }
        ]
      } as TableWidgetV2
    ]
  };
};
const newHighlightedPrice = ({ symbols }: TileValues): MarketApp => {
  return {
    id: 'tiles',
    name: '',
    metadata: null,
    type: 'tiles',
    tiles: symbols.active.map(symbol => {
      return {
        id: uniqueId(new Date().toISOString()),
        shortTitle: symbol.shortTitle,
        symbol: {
          key: symbol.symbol,
          group: 'Prod_Indexes'
        }
      };
    })
  };
};
const appFormAdapter = {
  // domain -> form
  toFormValues: (domain: WidgetV2 | TilesWidgetV2) => {
    const buildId = (key: SymbolKeyLike | undefined): string =>
      `${key?.Code ?? ''}-${key?.PeriodType ?? ''}-${key?.Period ?? ''}-${key?.TimeRef ?? ''}`;

    const seriesToSymbols = (series: SeriesLike[] = []) => {
      return series.map(s => ({
        id: buildId(s.symbol?.key),
        shortTitle: s.shortTitle ?? s.symbol?.key?.Code ?? '',
        symbol: {
          Code: s.symbol?.key?.Code ?? '',
          PeriodType: s.symbol?.key?.PeriodType ?? '',
          Period: s.symbol?.key?.Period ?? '',
          TimeRef: s.symbol?.key?.TimeRef ?? ''
        },
        group: 'Prod_Indexes' as const
      }));
    };
    const findColorPalette = (series: (TemplateChartSeries | FWDTemplateChartSeries)[]) => {
      const color = series[0]?.settings?.color;

      switch (color) {
        case vibrantPalette[0].hex:
          return ColorPalette.Vibrant;
        case monoBluePalette[0].hex:
          return ColorPalette.Monochrome;
        case balancedPalette[0].hex:
          return ColorPalette.Balanced;
        default:
          return ColorPalette.Vibrant;
      }
    };

    if (domain.type === 'tiles') {
      return {
        type: AppType.Tiles,
        symbols: {
          active: seriesToSymbols(domain.tiles),
          backlog: []
        }
      } as TileValues;
    }

    switch (domain.type) {
      case 'graph': {
        const graphWidget = domain;
        let type: AppType;
        if (graphWidget.metadata?.graphType === 'seasonal') {
          type = AppType.Seasonal;
        } else if (graphWidget.metadata?.graphType === 'forwardSimple') {
          type = AppType.FWD;
        } else {
          type = AppType.Spot;
        }

        const series = graphWidget.series ?? [];

        return {
          name: graphWidget.name,
          type,
          symbols: { active: seriesToSymbols(series), backlog: [] },
          colorPalette: findColorPalette(series),
          // from: type === AppType.Seasonal ? graphWidget.metadata?.from : undefined
          from: '2y'
        } as ChartFormValues;
      }
      case 'table': {
        const tableWidget = domain;
        return {
          type: AppType.Table,
          name: tableWidget.name,
          symbols: {
            active: seriesToSymbols(tableWidget.sections.flatMap(s => s.rows)),
            backlog: []
          }
        } as BasicTableValues;
      }
      default:
        throw new Error(`Unknown widget type: ${domain.type}`);
    }
  },

  // form -> domain (original is optional to preserve immutable fields like id)

  newApp: ({
    values
  }: {
    values: ChartFormValues | BasicTableValues | TileValues;
  }): MarketApp | undefined => {
    switch (values.type) {
      case AppType.Table:
        return newTable(values);
      case AppType.Tiles:
        return newHighlightedPrice(values);
      case AppType.Seasonal:
      case AppType.FWD:
      case AppType.Spot:
        return newChart(values);
      default:
        return undefined;
    }
  }
};

export default appFormAdapter;
